import {
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { Company } from 'lib-shared-types-api';
import { Component } from 'react';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';

import { pubSub } from 'utils/pubSub';

import InputField from '../../util/InputField';
import styles from '../style/companyModal.module.scss';

type Props = {
  initialValues: Company | null;
  meta: {
    asyncStatus: AsyncStatus;
  };
  ui: {
    isCompanyModalShown: boolean;
    cropperOpen: boolean;
    imageURI: string | null;
  };
  img: string | null;
  companyId: string | null;
  users: [];

  addCompany: (company: Company) => Promise<any>;
  editCompany: (payload: { companyId: string; patch: any }) => Promise<any>;

  updateAvatar: (file: string, company: Company) => void;
  hideModal: () => void;
  openAvatarCropperFileChange: (imageURI: string) => void;
  closeAvatarCropper: () => void;
  getPresigned: (imageURI: any, companyId: any) => void;
  getRelatedUsers: (companyId: string) => void;

  notificationAdd: (payload: any) => void;
} & InjectedFormProps<any, any, any>;
const validate = (values: any) => {
  const errors: { name?: string } = {};
  if (values === undefined || values === null) {
    return errors;
  }
  if (!values.name) {
    errors.name = 'Required';
  } else if (values.name.length <= 1) {
    errors.name = 'Must be 2 characters or more';
  }

  return errors;
};
const buildCompany = (values: any, companyId?: string | null): Company => {
  let company = {};

  if (typeof companyId !== 'string') {
    company = {
      name: values.name,
      user_ids: [],
    };
  } else {
    company = {
      updates: {
        name: values.name,
      },
      deletes: [],
    };
  }

  return company as Company;
};

class CompanyModal extends Component<Props> {
  constructor(props: Props) {
    super(props);
    this.handleSaveClick = this.handleSaveClick.bind(this);
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.ui.isCompanyModalShown === false &&
      this.props.ui.isCompanyModalShown === true
    ) {
      const { companyId } = this.props;
      if (
        companyId !== undefined &&
        companyId !== null &&
        typeof companyId === 'string'
      ) {
        this.props.getRelatedUsers(companyId); // fetch users.
      }
    }
  }

  componentDidMount() {
    const { companyId } = this.props;

    if (
      companyId !== undefined &&
      companyId !== null &&
      typeof companyId === 'string'
    ) {
      this.props.getRelatedUsers(companyId); // fetch users.
    }
  }

  handleSaveClick(values: any) {
    const { companyId, addCompany, editCompany, destroy, hideModal } =
      this.props;

    ampli.companyModalFormSubmitted({
      mode: companyId === null ? 'add' : 'edit',
    });

    if (companyId === null) {
      addCompany(buildCompany(values))
        .then(() => {
          const name = values.name ? values.name : '';
          const options = {
            title: 'Company saved',
            text: `The company "${name}" is saved`,
            className: 'success',
            time: 5000,
            icon: 'i-done',
          };
          this.props.notificationAdd(options);
          destroy();
          hideModal();
          pubSub.publish('companyModalCompanyAdded');
        })
        .catch((e) => {
          console.warn('Error', e);
          const options = {
            title: 'En error occurred',
            text: 'Company could not be saved',
            className: 'danger',
            icon: 'i-exclamation',
          };
          this.props.notificationAdd(options);
        });
    } else {
      editCompany({
        companyId: companyId,
        patch: buildCompany(values, companyId),
      })
        .then(() => {
          const name = values.name ? values.name : '';
          const options = {
            title: 'Company saved',
            text: `The company "${name}" is updated`,
            className: 'success',
            time: 5000,
            icon: 'i-done',
          };
          this.props.notificationAdd(options);
          destroy();
          hideModal();
        })
        .catch((e) => {
          console.warn('Error', e);
          const options = {
            title: 'En error occurred',
            text: 'Company could not be updated',
            className: 'danger',
            icon: 'i-exclamation',
          };
          this.props.notificationAdd(options);
        });
    }
  }

  render() {
    let labelClass = 'hidden';

    const {
      initialValues,
      meta,
      ui,
      companyId,
      hideModal,
      handleSubmit,
      asyncValidating,
      destroy,
      pristine,
    } = this.props;

    if (pristine === false && initialValues !== null) {
      labelClass = '';
    }
    return (
      <Modal
        className={styles.modal}
        isOpen={ui.isCompanyModalShown}
        onClose={() => {
          destroy();
          hideModal();
        }}
      >
        <ModalHead
          title={
            <>
              {companyId === null
                ? 'Add a new company'
                : 'Edit ' +
                  (typeof initialValues.name === 'string'
                    ? initialValues.name
                    : '')}
              <label
                className={
                  'unsaved-changes-label label label-danger clickable-cursor ' +
                  labelClass
                }
              >
                Unsaved changes
              </label>
            </>
          }
          onClose={() => {
            destroy();
            hideModal();
          }}
        />

        <ModalBody>
          <form
            id="companyModalForm"
            onSubmit={handleSubmit(this.handleSaveClick)}
          >
            <FlexBox>
              <Field
                displayName="Name"
                name="name"
                component={InputField}
                type="text"
                className="mb0"
                placeholder="Company name..."
              />
            </FlexBox>
          </form>
        </ModalBody>

        <ModalFoot
          confirmName={
            companyId === null
              ? `Add new company`
              : `Save ${
                  typeof initialValues.name === 'string'
                    ? initialValues.name
                    : ''
                }`
          }
          confirmProps={{
            disabled:
              asyncValidating !== false || meta.asyncStatus === 'loading'
                ? true
                : false,
            type: 'submit',
            form: 'companyModalForm',
          }}
          confirmOnClick={() => {
            // No action needed.
          }}
          cancelOnClick={() => {
            destroy();
            hideModal();
          }}
        />
      </Modal>
    );
  }
}

export default reduxForm<any, any, any>({
  form: 'companyModalForm',
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  validate,
})(CompanyModal);
